import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import {
  Button,
  Collapse,
  Fade,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import { includes } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';

import { ActionDialogType, useActiveActionDialogType } from 'components/ActionDialog';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import ErrorSnackbar from 'components/ErrorSnackbar';
import NoCasesFound from 'components/SearchFilters/MainFilters/NoCasesFound';
import SaveCohortModal from 'components/SearchFilters/SaveCohortModal';
import StudyExportMenu from 'components/StudiesDashboard/StudyExportMenu/StudyExportMenu';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { Study } from 'interfaces/study';
import { useForms } from 'utils/queryHooks/form/useForm';
import { useCurrentStateCases } from 'utils/useCurrentStateCases';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { ExperimentResultsSelection } from 'utils/useEncodedFilters/types';
import { usePermissions } from 'utils/usePermissions';
import SelectedCardsDrawer from '../SelectedCardsDrawer';
import { shouldShowPrepareCustomerResults, shouldShowStudyExport } from '../studyActionAvailability';
import { CasesDisplay } from './CasesDisplay';
import { DatasetModeSelector } from './DatasetModeSelector';
import { DatasetWorkflowStatus } from './DatasetWorkflowStatus';
import { FiltersAndExport } from './FiltersAndExports';
import { PageStats } from './PagesStats';
import { canQueryPendingSlides } from './pendingSlidesPermissions';
import SelectAllCardsCheckbox from './ProcedureCard/Carousel/AllCardsCheckbox';
import { ProcedurePageLayoutControls } from './ProcedurePageLayoutControls';
import { useCasesViewMode } from './useCasesViewMode';
import { usePendingSlides } from './usePendingSlides';

const procedureViewToggleButtonSx = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 40,
  px: 1.5,
};

const EMPTY_CASE_CHROME_COLLAPSE_DURATION = 240;
const EMPTY_CASE_PANEL_REVEAL_DURATION = 180;

export const getProcedureControlsSpacing = (compactLaptopControls: boolean) => ({
  sectionSpacing: compactLaptopControls ? 0.5 : 1,
  sectionMarginY: compactLaptopControls ? 0.5 : 1,
  containerColumnGap: compactLaptopControls ? '0.75rem' : '1rem',
  containerRowGap: compactLaptopControls ? '0.5rem' : '1rem',
  groupSpacing: compactLaptopControls ? 1 : 2,
  actionSpacing: compactLaptopControls ? 0.75 : 1.5,
  statsRowGap: compactLaptopControls ? 0.25 : 0.5,
  selectAllPaddingBottom: compactLaptopControls ? 0.5 : 1,
  selectAllMarginBottom: compactLaptopControls ? 0.5 : 1,
});

interface Props {
  displayProcedureModeToggle?: boolean;
  study?: Study;
}

const ProceduresPage = ({ displayProcedureModeToggle, study }: Props) => {
  const activeStudyId = study?.id;
  const qcLabelConfig = study?.qcLabelsConfig;
  const allowExport = study?.allowExport;

  const { hasPermission } = usePermissions();

  const { casesViewMode, setCasesViewMode } = useCasesViewMode();
  const { fieldsContext } = useTableEditingContext<Procedure, ProceduresFieldsContext>();

  const canUseExperimentalFeatures = hasPermission(Permission.UseNucleaiExperimentalFeatures);
  const canPrepareApprovedCustomerResultsForDownload = hasPermission(
    Permission.PrepareApprovedCustomerResultsForDownload
  );
  const procedureViewMode = displayProcedureModeToggle ? casesViewMode : 'card';
  const canUseGalleryMode = displayProcedureModeToggle && canUseExperimentalFeatures;
  const theme = useTheme();
  const keepSingleLineControls = useMediaQuery(theme.breakpoints.up('md'));
  const compactLaptopControls = useMediaQuery(theme.breakpoints.between('md', 'xl'));
  const procedureControlsSpacing = getProcedureControlsSpacing(compactLaptopControls);

  const [isSaveCohortModalOpen, setIsSaveCohortModalOpen] = useState(false);
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);
  const { queryParams, convertToQueryObject } = useEncodedFilters({
    experimentResultsSelection: ExperimentResultsSelection.OnlyQAFailed,
  });

  const queryObject = useMemo(() => convertToQueryObject(queryParams), [queryParams]);

  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);

  const canViewStudyPendingSlides = canQueryPendingSlides({ hasPermission, studyId: activeStudyId });

  const { totalPendingSlides } = usePendingSlides();

  const {
    isLoading: casesLoading,
    totalCasesCount: totalCases,
    totalSlidesCount: totalSlides,
    procedures: pageCases,
    slidesInPreviousPages,
    casesInPreviousPages,
    error,
  } = useCurrentStateCases();

  const isStudyExists = queryParams.filters?.studyId && queryParams.filters.studyId !== 'none';
  const shouldShowWorkflowStatus =
    Boolean(study?.id) &&
    !fieldsContext.stainTypesInStudyLoading &&
    includes(fieldsContext.stainTypesInStudy, MULTIPLEX_STAIN_ID);

  const numRows = slidesMode ? totalSlides : totalCases;
  const hasCaseOrSlideResults = totalCases > 0 || totalSlides > 0;
  const isEmptyCaseStudyLayout =
    Boolean(study?.id) &&
    study.casesCount === 0 &&
    totalCases === 0 &&
    !casesLoading &&
    !error &&
    !queryParams.cohortId &&
    !pendingSlidesMode;
  const [collapseDatasetChrome, setCollapseDatasetChrome] = useState(false);
  const [hasCollapsedEmptyStudyChrome, setHasCollapsedEmptyStudyChrome] = useState(false);
  useEffect(() => {
    setCollapseDatasetChrome(isEmptyCaseStudyLayout);
    if (!isEmptyCaseStudyLayout) {
      setHasCollapsedEmptyStudyChrome(false);
    }
  }, [isEmptyCaseStudyLayout]);
  const { formsWithViewableResponses } = useForms({ studyId: activeStudyId }, { enabled: numRows > 0 });

  const [, setCurrentActionItemType] = useActiveActionDialogType();

  return (
    <>
      <SelectedCardsDrawer
        displayProcedureModeToggle={displayProcedureModeToggle}
        workflowStatus={
          shouldShowWorkflowStatus && procedureViewMode !== 'table' && !collapseDatasetChrome ? (
            <DatasetWorkflowStatus />
          ) : undefined
        }
      />
      {error && <ErrorSnackbar message={error.toString()} />}

      <SaveCohortModal
        isOpen={isSaveCohortModalOpen}
        onClose={() => setIsSaveCohortModalOpen(false)}
        queryObject={queryObject}
      />

      <Grid
        container
        direction="column"
        spacing={procedureControlsSpacing.sectionSpacing}
        my={procedureControlsSpacing.sectionMarginY}
        height={procedureViewMode === 'table' ? '100%' : 'auto'}
        wrap="nowrap"
      >
        {!isEmptyCaseStudyLayout || !hasCollapsedEmptyStudyChrome ? (
          <Grid
            item
            xs={procedureViewMode === 'table' ? undefined : 12}
            data-testid="dataset-controls-collapse"
            sx={{ flex: procedureViewMode === 'table' ? '0 0 auto' : undefined }}
          >
            <Collapse
              in={!collapseDatasetChrome}
              timeout={EMPTY_CASE_CHROME_COLLAPSE_DURATION}
              unmountOnExit
              onExited={() => setHasCollapsedEmptyStudyChrome(true)}
              onEntered={() => setHasCollapsedEmptyStudyChrome(false)}
            >
              <Grid container direction="column" spacing={procedureControlsSpacing.sectionSpacing} wrap="nowrap">
                <FiltersAndExport
                  qcLabelConfig={qcLabelConfig}
                  totalCases={totalCases}
                  setIsSaveCohortModalOpen={setIsSaveCohortModalOpen}
                />
                <Grid
                  item
                  container
                  justifyContent="space-between"
                  wrap={keepSingleLineControls ? 'nowrap' : 'wrap'}
                  sx={{
                    position: 'relative',
                    columnGap: procedureControlsSpacing.containerColumnGap,
                    rowGap: procedureControlsSpacing.containerRowGap,
                  }}
                >
                  <Grid
                    item
                    container
                    alignItems="center"
                    spacing={procedureControlsSpacing.groupSpacing}
                    wrap={keepSingleLineControls ? 'nowrap' : 'wrap'}
                    sx={{ minWidth: 0, flex: { xs: '1 1 100%', md: '0 1 auto' } }}
                  >
                    {displayProcedureModeToggle && (
                      <Grid item>
                        <ToggleButtonGroup
                          size="small"
                          color="primary"
                          value={procedureViewMode}
                          exclusive
                          sx={{ '& .MuiToggleButton-root': procedureViewToggleButtonSx }}
                          onChange={(_, newViewMode) => {
                            if (!newViewMode) {
                              return;
                            }

                            setCasesViewMode(newViewMode);
                          }}
                          aria-label="Procedure-View-Mode"
                        >
                          <ToggleButton value="table" data-cy="cases table view">
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <TableRowsOutlinedIcon fontSize="small" />
                              <span>Table</span>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton value="card" data-cy="cases cards view">
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <DashboardOutlinedIcon fontSize="small" />
                              <span>Cards</span>
                            </Stack>
                          </ToggleButton>
                          {canUseGalleryMode && (
                            <ToggleButton value="gallery" data-cy="cases gallery view">
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <PhotoLibraryOutlinedIcon fontSize="small" />
                                <span>Gallery</span>
                              </Stack>
                            </ToggleButton>
                          )}
                        </ToggleButtonGroup>
                      </Grid>
                    )}
                    <Grid item>
                      <DatasetModeSelector
                        showNeedsAssignment={
                          Boolean(activeStudyId) && canViewStudyPendingSlides && totalPendingSlides > 0
                        }
                        needsAssignmentCount={totalPendingSlides}
                      />
                    </Grid>
                    {procedureViewMode !== 'table' && (
                      <Grid item>
                        <ProcedurePageLayoutControls compact={compactLaptopControls} />
                      </Grid>
                    )}
                  </Grid>
                  {shouldShowWorkflowStatus && procedureViewMode === 'table' && (
                    <Grid
                      item
                      data-testid="dataset-workflow-status-slot"
                      sx={{
                        minWidth: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        flex: keepSingleLineControls ? '0 0 auto' : '1 1 100%',
                        position: keepSingleLineControls ? 'absolute' : 'static',
                        left: keepSingleLineControls ? '50%' : 'auto',
                        transform: keepSingleLineControls ? 'translateX(-50%)' : 'none',
                        zIndex: keepSingleLineControls ? 1 : 'auto',
                      }}
                    >
                      <DatasetWorkflowStatus />
                    </Grid>
                  )}
                  <Grid
                    container
                    item
                    alignItems="center"
                    justifyContent="end"
                    wrap={keepSingleLineControls ? 'nowrap' : 'wrap'}
                    spacing={procedureControlsSpacing.actionSpacing}
                    sx={{ minWidth: 0, width: 'auto', flex: '0 0 auto' }}
                  >
                    {canPrepareApprovedCustomerResultsForDownload && shouldShowPrepareCustomerResults(study) && (
                      <Grid item>
                        <Tooltip title="Prepare customer approved results for download" placement="top">
                          <span>
                            <Button
                              size="small"
                              color="primary"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setCurrentActionItemType(ActionDialogType.PrepareCustomerResults);
                              }}
                            >
                              {compactLaptopControls ? 'Prepare results' : 'Prepare customer results for download'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Grid>
                    )}

                    {shouldShowStudyExport(study) && (
                      <Grid item>
                        <StudyExportMenu
                          forms={formsWithViewableResponses}
                          study={study}
                          disabled={!isStudyExists || !allowExport}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                {casesLoading || hasCaseOrSlideResults ? (
                  <Grid
                    container
                    item
                    xs={12}
                    justifyContent="space-between"
                    wrap={keepSingleLineControls ? 'nowrap' : 'wrap'}
                    style={{ flex: 0 }}
                    sx={{ rowGap: procedureControlsSpacing.statsRowGap }}
                  >
                    <Grid item sx={{ minWidth: 0 }}>
                      <PageStats
                        totalCases={totalCases}
                        totalSlides={totalSlides}
                        slidesInPreviousPages={slidesInPreviousPages}
                        proceduresInPreviousPages={casesInPreviousPages}
                        pageCases={pageCases}
                        casesLoading={casesLoading}
                        pendingSlidesMode={pendingSlidesMode}
                      />
                    </Grid>
                  </Grid>
                ) : null}

                {casesViewMode != 'table' && numRows > 0 && (
                  <Grid container sx={{ px: 2, pb: 1, mb: 1 }}>
                    <Grid item container>
                      <Grid item>
                        <SelectAllCardsCheckbox numRows={numRows} />
                      </Grid>

                      <Grid item sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography>Select All</Typography>{' '}
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Collapse>
          </Grid>
        ) : null}
        <Grid
          item
          xs={procedureViewMode === 'table' ? undefined : 12}
          sx={procedureViewMode === 'table' ? { display: 'flex', flex: '1 1 0%', minHeight: 0 } : undefined}
        >
          <Fade in={!isEmptyCaseStudyLayout || hasCollapsedEmptyStudyChrome} timeout={EMPTY_CASE_PANEL_REVEAL_DURATION}>
            <Grid
              container
              direction="column"
              sx={{
                minWidth: 0,
                minHeight: 0,
                ...(procedureViewMode === 'table' && { flex: '1 1 auto', height: '100%' }),
              }}
            >
              <CasesDisplay
                study={study}
                displayProcedureModeToggle={displayProcedureModeToggle}
                pendingSlidesMode={pendingSlidesMode}
                emptyStateRevealReady={!isEmptyCaseStudyLayout || hasCollapsedEmptyStudyChrome}
                hidePagination={totalCases === 0 && totalSlides === 0}
                forms={formsWithViewableResponses}
              />
              {!casesLoading && totalCases === 0 && <NoCasesFound />}
            </Grid>
          </Fade>
        </Grid>
      </Grid>
    </>
  );
};

export default ProceduresPage;
